import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reviews from '../components/Reviews';
import ProductCard from '../components/products/ProductCard';
import { imgUrl } from '../utils/imgUrl';
import { product_details } from '../store/reducers/homeReducer';
import { add_to_card, messageClear, add_to_wishlist } from '../store/reducers/cardReducer';
import {
    DISPLAY, Container, formatQ, priceLabel, Heart, Bag, Truck, Undo, ZoomIn, LinkIcon,
    Facebook, WhatsApp, Close, Check,
} from '../components/home/ui';

// Ficha de producto (diseño producto.html de Claude Design).

const starsText = (n) => {
    const full = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
    return '★'.repeat(full) + '☆'.repeat(5 - full);
};

const Details = () => {

    const navigate = useNavigate()
    const {slug} = useParams()
    const dispatch = useDispatch()
    const {product: base, relatedProducts, moreProducts, totalReview} = useSelector(state => state.home)
    const {userInfo } = useSelector(state => state.auth)
    const {errorMessage, successMessage, wishlist } = useSelector(state => state.card)

    // Variante elegida (talla / color / estilo). El producto que se muestra es
    // el base con id, precio, stock y fotos de esa variante: si no tiene fotos
    // propias el backend ya manda las compartidas, así que aquí no hay fallback.
    const variants = useMemo(() => base?.variants || [], [base])
    const [selectedId, setSelectedId] = useState(null)
    const [image, setImage] = useState('')
    const [quantity, setQuantity] = useState(1)
    const [tab, setTab] = useState('desc')
    const [zoom, setZoom] = useState(false)
    const [favAdded, setFavAdded] = useState(false)
    useEffect(() => { setSelectedId(null); setImage(''); setQuantity(1); setTab('desc'); setFavAdded(false) }, [slug])
    const selected = variants.find(v => v.id === selectedId) || variants.find(v => v.id === base?._id) || variants[0]
    const product = useMemo(() => {
        if (!base || !selected) return base || {}
        return { ...base, _id: selected.id, id: selected.id, sku: selected.sku,
                 price: selected.price, stock: selected.stock,
                 images: selected.images?.length ? selected.images : base.images,
                 image: selected.images?.[0] || base.image }
    }, [base, selected])
    // Bajo pedido (ECO-05): se puede comprar aunque el disponible sea 0; se
    // pide al proveedor y se promete el plazo en días hábiles.
    const bajoPedido = !!product.bajo_pedido && !(product.stock > 0)
    const comprable = product.stock > 0 || bajoPedido

    // Dimensiones disponibles, en el orden en que vienen las variantes.
    const dimensiones = useMemo(() => {
        const unicos = (fn) => variants.reduce((acc, v) => {
            const k = fn(v); if (k && !acc.some(x => x.key === k)) acc.push({ key: k, v }); return acc
        }, [])
        return {
            talla:  unicos(v => v.talla),
            color:  unicos(v => v.color?.name),
            estilo: unicos(v => v.estilo),
        }
    }, [variants])
    const hayVariantes = variants.length > 1
    const preciosDistintos = new Set(variants.map(v => v.price)).size > 1

    // **Miniatura por opción, estilo Amazon/Temu — pero solo si informa algo.**
    // El backend ya devuelve en `images` las fotos propias de la variante y, si
    // no tiene, las compartidas del producto: desde acá no se puede distinguir
    // una de otra. Lo que sí se puede ver es si las opciones **se ven
    // distintas**, y es la pregunta que importa: si las nueve tallas comparten
    // la galería del producto, nueve miniaturas idénticas no dicen nada y solo
    // tapan el nombre de la talla. Se dibujan cuando hay más de una foto
    // distinta entre las opciones de esa dimensión.
    const conFoto = useMemo(() => {
        const out = {}
        for (const dim of ['talla', 'color', 'estilo']) {
            const primeras = (dimensiones[dim] || []).map(({ v }) => v.images?.[0] || '')
            out[dim] = new Set(primeras.filter(Boolean)).size > 1
        }
        return out
    }, [dimensiones])
    const valorDe = (v, dim) => dim === 'color' ? v.color?.name : v[dim]

    // Al tocar un botón se busca la variante que mantiene las otras
    // dimensiones; si esa combinación no existe se toma cualquiera con ese valor.
    const candidata = (dim, key) => {
        const coincide = (v, d) => d === dim ? valorDe(v, d) === key : valorDe(v, d) === valorDe(selected, d)
        const exacta = variants.find(v => ['talla', 'color', 'estilo'].every(d => !valorDe(selected, d) || coincide(v, d)))
        return exacta || variants.find(v => valorDe(v, dim) === key)
    }
    const elegir = (dim, key) => {
        const v = candidata(dim, key)
        if (v) { setSelectedId(v.id); setImage(''); setQuantity(1) }
    }
    // Un valor está agotado si ninguna variante con ese valor tiene stock.
    const agotado = (dim, key) => !variants.some(v => valorDe(v, dim) === key && v.stock > 0)

    const [loadedSlug, setLoadedSlug] = useState(null)
    useEffect(() => {
        dispatch(product_details(slug)).then(() => setLoadedSlug(slug))
        window.scrollTo(0, 0)
    },[slug])

    useEffect(() => {
        if (!zoom) return
        const onKey = (e) => { if (e.key === 'Escape') setZoom(false) }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [zoom])

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage)
            dispatch(messageClear())
        }
        if (errorMessage) {
            toast.error(errorMessage)
            dispatch(messageClear())
        }
    },[successMessage,errorMessage])

    const maxQty = bajoPedido ? Infinity : Math.max(1, Math.floor(product.stock || 0))

    const inc = () => {
        if (!bajoPedido && quantity >= product.stock) {
            toast.error('No hay más unidades disponibles')
        } else {
            setQuantity(quantity + 1)
        }
    }

    const dec = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1)
        }
    }

    const add_card = () => {
        if (userInfo) {
           dispatch(add_to_card({
            userId: userInfo.id,
            quantity,
            productId : product._id
           }))
        } else {
            navigate('/login')
        }
    }

    const enWishlist = favAdded || wishlist.some(w => w.productId === product._id)

    const add_wishlist = () => {
        if (!userInfo) { navigate('/login'); return }
        if (enWishlist) { navigate('/dashboard/my-wishlist'); return }
        dispatch(add_to_wishlist({
            userId: userInfo.id,
            productId: product._id,
            name: product.name,
            price: product.price,
            image: imgUrl(product.images[0]),
            discount: product.discount,
            rating: product.rating,
            slug: product.slug
        }))
        setFavAdded(true)
    }

   const buynow = () => {
        let price = 0;
        if (product.discount !== 0) {
            price = product.price - Math.floor((product.price * product.discount) / 100)
        } else {
            price = product.price
        }

        const obj = [
            {
                sellerId: product.sellerId,
                shopName: product.shopName,
                price :  quantity * (price - Math.floor((price * 5) / 100)),
                products : [
                    {
                        quantity,
                        productInfo: product
                    }
                ]
            }
        ]

        navigate('/shipping',{
            state: {
                products : obj,
                price: price * quantity,
                shipping_fee : 50,
                items: 1
            }
        })
   }

    // "Complétalo con": productos de la misma categoría, que se agregan al
    // carrito junto con este. No hay descuento por combo en el sistema.
    const sugeridos = useMemo(
        () => relatedProducts.filter(p => p._id !== product._id).slice(0, 3),
        [relatedProducts, product._id]
    )
    const [comboOff, setComboOff] = useState({})
    useEffect(() => { setComboOff({}) }, [slug])
    const comboSel = sugeridos.filter(p => !comboOff[p._id])
    const add_combo = () => {
        if (!userInfo) { navigate('/login'); return }
        dispatch(add_to_card({ userId: userInfo.id, quantity, productId: product._id }))
        comboSel.forEach(p => dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: p._id })))
    }

    const relacionados = (relatedProducts.length ? relatedProducts : moreProducts).filter(p => p._id !== product._id).slice(0, 4)

    const share = (red) => {
        const url = window.location.href
        if (red === 'copy') {
            navigator.clipboard?.writeText(url)
                .then(() => toast.success('Enlace copiado'))
                .catch(() => toast.error('No se pudo copiar el enlace'))
            return
        }
        const href = red === 'wa'
            ? `https://wa.me/?text=${encodeURIComponent(`${product.name} ${url}`)}`
            : `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        window.open(href, '_blank', 'noopener,noreferrer')
    }

    const verResenas = (e) => {
        e.preventDefault()
        setTab('rev')
        document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const images = product.images?.filter(Boolean) || []
    const current = image || images[0]
    const precio = product.discount > 0 ? product.price - Math.floor((product.price * product.discount) / 100) : product.price
    const noExiste = loadedSlug === slug && (!base?.name || base.slug !== slug)
    const cargando = !noExiste && (!base?.name || base.slug !== slug)

    const disponibilidad = product.stock > 0
        ? { cls: 'text-[#16a34a]', dot: 'bg-[#16a34a]', txt: 'En stock' }
        : bajoPedido
            ? { cls: 'text-[#b45309]', dot: 'bg-[#fbbf24]', txt: 'Bajo pedido' }
            : { cls: 'text-[#ef4444]', dot: 'bg-[#ef4444]', txt: 'Agotado' }

    const specs = [
        ['Marca', product.brand],
        ['Categoría', product.category],
        ['Talla', selected?.talla],
        ['Color', selected?.color?.name],
        ['Estilo', selected?.estilo],
        ['SKU', product.sku],
        ['Disponibilidad', product.stock > 0 ? `${product.stock} en stock`
            : bajoPedido ? `Bajo pedido · hasta ${product.dias_entrega} día${product.dias_entrega === 1 ? '' : 's'} hábil${product.dias_entrega === 1 ? '' : 'es'}`
            : 'Agotado'],
    ].filter(([, v]) => v !== undefined && v !== null && v !== '')

    const reviewCount = totalReview || product.reviewCount || 0

    return (
        <div className='w-full bg-white'>
            <Header/>

            {noExiste ? (
                <Container className='py-24 text-center'>
                    <div className='text-[64px] opacity-40 mb-4'>🐾</div>
                    <h1 className='font-bold text-[#1e3a8a] text-[32px] m-0' style={DISPLAY}>No encontramos este producto</h1>
                    <p className='text-[#64748b] mt-2 mb-6'>Puede que ya no esté disponible.</p>
                    <Link to='/shops' className='inline-flex items-center px-[22px] py-[14px] rounded-[14px] font-bold text-[15px] text-white bg-[#0891b2] hover:bg-[#0e7490]'>Ver la tienda</Link>
                </Container>
            ) : cargando ? (
                <Container className='py-24'>
                    <div className='grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md-lg:grid-cols-1 gap-14 animate-pulse'>
                        <div className='rounded-[24px] bg-[#faf9f6]' style={{ aspectRatio: '1 / 1' }} />
                        <div className='flex flex-col gap-4'>
                            <div className='h-3 w-24 rounded bg-[#f1efea]' />
                            <div className='h-10 w-4/5 rounded bg-[#f1efea]' />
                            <div className='h-24 rounded-[18px] bg-[#faf9f6]' />
                        </div>
                    </div>
                </Container>
            ) : (
            <>
            <Container>
                <nav className='py-[18px] text-[13px] text-[#64748b] flex gap-2 items-center flex-wrap' aria-label='Ruta'>
                    <Link to='/' className='hover:text-[#0891b2]'>Inicio</Link>
                    <span>/</span>
                    {product.category && <>
                        <Link to={`/products?category=${product.category}`} className='hover:text-[#0891b2]'>{product.category}</Link>
                        <span>/</span>
                    </>}
                    <span className='text-[#0f172a] font-semibold'>{product.name}</span>
                </nav>

                <section className='grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md-lg:grid-cols-1 gap-14 md-lg:gap-8 pt-2 pb-16 items-start'>
                    {/* ── Galería ── */}
                    <div className={`grid gap-[14px] sticky top-[150px] md-lg:static ${images.length > 1 ? 'grid-cols-[84px_minmax(0,1fr)] sm:grid-cols-1' : 'grid-cols-1'}`}>
                        {images.length > 1 && (
                            <div className='flex flex-col sm:flex-row sm:order-2 gap-[10px] sm:overflow-x-auto'>
                                {images.slice(0, 6).map((img, i) => (
                                    <button key={img + i} type='button' onClick={() => setImage(img)} aria-label={`Foto ${i + 1}`}
                                        className={`w-[84px] flex-shrink-0 rounded-[14px] overflow-hidden border-[1.5px] bg-[#faf9f6] transition-colors ${img === current ? 'border-[#0891b2] shadow-[0_0_0_3px_rgba(8,145,178,.12)]' : 'border-[#f1efea] hover:border-[#cffafe]'}`}
                                        style={{ aspectRatio: '1 / 1' }}>
                                        <img src={imgUrl(img)} alt='' className='w-full h-full object-cover' />
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className='relative rounded-[24px] overflow-hidden bg-[#faf9f6]' style={{ aspectRatio: '1 / 1' }}>
                            {current
                                ? <img src={imgUrl(current)} alt={product.name} className='absolute inset-0 w-full h-full object-cover' />
                                : <span className='absolute inset-0 grid place-items-center text-[80px] opacity-30'>🐾</span>}
                            <div className='absolute top-[18px] left-[18px] flex gap-[6px] z-[2]'>
                                {product.discount > 0 && <span className='text-[10.5px] font-extrabold px-[9px] py-[4px] rounded-full uppercase tracking-[.04em] bg-[#f87171] text-white whitespace-nowrap'>-{product.discount}%</span>}
                                {bajoPedido && <span className='text-[10.5px] font-extrabold px-[9px] py-[4px] rounded-full uppercase tracking-[.04em] bg-[#fbbf24] text-[#1e3a8a] whitespace-nowrap'>Bajo pedido</span>}
                                {!comprable && <span className='text-[10.5px] font-extrabold px-[9px] py-[4px] rounded-full uppercase tracking-[.04em] bg-[#64748b] text-white whitespace-nowrap'>Agotado</span>}
                            </div>
                            {current && (
                                <button type='button' onClick={() => setZoom(true)} aria-label='Ampliar foto'
                                    className='absolute bottom-[18px] right-[18px] w-11 h-11 rounded-full bg-white grid place-items-center text-[#1e3a8a] shadow-[0_6px_18px_-6px_rgba(15,23,42,.08)] z-[2] hover:text-[#0891b2]'>
                                    <ZoomIn className='w-[18px] h-[18px]' />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Información ── */}
                    <div>
                        {product.brand && <span className='text-[12px] font-extrabold tracking-[.08em] uppercase text-[#0891b2]'>{product.brand}</span>}
                        <h1 className='font-bold text-[#1e3a8a] leading-[1.08] tracking-[-.02em] mt-2 mb-3' style={{ ...DISPLAY, fontSize: 'clamp(28px, 3vw, 38px)', textWrap: 'balance' }}>
                            {product.name}
                        </h1>
                        <div className='flex items-center gap-[10px] text-[13.5px] text-[#64748b] flex-wrap'>
                            <span className='text-[#fbbf24] tracking-[1px] text-[15px]'>{starsText(product.rating)}</span>
                            <span>{(Number(product.rating) || 0).toFixed(1)}</span>
                            <a href='#reviews' onClick={verResenas} className='text-[#0891b2] font-semibold'>
                                {reviewCount} reseña{reviewCount === 1 ? '' : 's'}
                            </a>
                            <span className='w-1 h-1 rounded-full bg-[#e8e6e1]' />
                            <span className={`inline-flex items-center gap-[6px] font-bold ${disponibilidad.cls}`}>
                                <span className={`w-[7px] h-[7px] rounded-full ${disponibilidad.dot}`} />{disponibilidad.txt}
                            </span>
                        </div>

                        <div className='my-[22px] px-[22px] py-5 bg-[#faf9f6] border border-[#f1efea] rounded-[18px] flex items-center gap-[14px] flex-wrap'>
                            <span className={`font-extrabold text-[#1e3a8a] tracking-[-.02em] leading-none ${precio > 0 ? 'text-[38px]' : 'text-[26px]'}`} style={DISPLAY}>{priceLabel(precio)}</span>
                            {product.discount > 0 && <>
                                <span className='text-[17px] text-[#64748b] line-through'>{formatQ(product.price)}</span>
                                <span className='bg-[#f87171] text-white text-[12px] font-extrabold px-[10px] py-[5px] rounded-full'>Ahorras {formatQ(product.price - precio)}</span>
                            </>}
                            {bajoPedido && (
                                <div className='basis-full text-[13px] text-[#334155]'>
                                    Se pide al proveedor: llega en <b className='text-[#1e3a8a]'>hasta {product.dias_entrega} día{product.dias_entrega === 1 ? '' : 's'} hábil{product.dias_entrega === 1 ? '' : 'es'}</b>.
                                </div>
                            )}
                        </div>

                        {hayVariantes && [['talla', 'Talla'], ['color', 'Color'], ['estilo', 'Estilo']].map(([dim, label]) => (
                            dimensiones[dim].length > 0 && (
                                <div key={dim} className='mb-5'>
                                    <div className='flex justify-between text-[13px] font-bold text-[#0f172a] mb-[10px]'>
                                        {label} <span className='text-[#64748b] font-medium'>{valorDe(selected, dim)}</span>
                                    </div>
                                    <div className='flex gap-2 flex-wrap' role='radiogroup' aria-label={label}>
                                        {dimensiones[dim].map(({ key, v }) => {
                                            const activo = valorDe(selected, dim) === key
                                            const sinStock = agotado(dim, key)
                                            const hex = dim === 'color' ? v.color?.hex : null
                                            const cand = candidata(dim, key)
                                            const miniatura = conFoto[dim] ? (v.images?.[0] || null) : null
                                            return (
                                                <button key={key} type='button' role='radio' aria-checked={activo}
                                                    disabled={sinStock} onClick={() => elegir(dim, key)}
                                                    title={sinStock ? `${key} — agotado` : key}
                                                    className={`rounded-[12px] border-[1.5px] text-[14px] font-semibold text-[#0f172a] transition-all
                                                        ${miniatura ? 'p-[6px] flex flex-col items-center gap-[5px] w-[86px]'
                                                                    : 'px-[18px] py-[11px] flex flex-col items-start gap-[2px]'} leading-[1.2]
                                                        ${activo ? 'border-[#0891b2] bg-[#ecfeff] shadow-[0_0_0_3px_rgba(8,145,178,.1)]' : 'border-[#e8e6e1] bg-white hover:border-[#0891b2]'}
                                                        ${sinStock ? 'opacity-45 cursor-not-allowed hover:border-[#e8e6e1]' : ''}`}>
                                                    {/* La foto de la opción. `alt` vacío a propósito: el
                                                        nombre va escrito debajo y leerlo dos veces con
                                                        lector de pantalla no agrega nada. */}
                                                    {miniatura && (
                                                        <span className='block w-full aspect-square rounded-[8px] overflow-hidden bg-[#faf9f6]'>
                                                            <img src={imgUrl(miniatura)} alt='' loading='lazy'
                                                                className='w-full h-full object-cover' />
                                                        </span>
                                                    )}
                                                    <span className={`inline-flex items-center gap-2 ${miniatura ? 'text-[13px]' : ''} ${sinStock ? 'line-through' : ''}`}>
                                                        {hex && <span className='w-4 h-4 rounded-full border border-[#e8e6e1] inline-block' style={{ backgroundColor: hex }} aria-hidden='true' />}
                                                        {key}
                                                    </span>
                                                    {sinStock
                                                        ? <small className='text-[11.5px] text-[#64748b] font-medium'>Agotado</small>
                                                        : preciosDistintos && cand && cand.price > 0 && <small className='text-[11.5px] text-[#64748b] font-medium'>{formatQ(cand.price)}</small>}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        ))}

                        {comprable ? (
                            <>
                                <div className='grid grid-cols-[auto_minmax(0,1fr)_auto] gap-[10px] mt-6 mb-3'>
                                    <div className='flex items-center border-[1.5px] border-[#e8e6e1] rounded-[14px] h-[54px] bg-white'>
                                        <button type='button' onClick={dec} aria-label='Quitar uno' className='w-11 h-full text-[18px] text-[#1e3a8a] font-semibold hover:text-[#0891b2]'>−</button>
                                        <input value={quantity} aria-label='Cantidad' inputMode='numeric'
                                            onChange={e => {
                                                const n = parseInt(e.target.value.replace(/\D/g, ''), 10)
                                                setQuantity(Number.isNaN(n) ? 1 : Math.min(Math.max(1, n), maxQty))
                                            }}
                                            className='w-10 border-none text-center text-[16px] font-bold text-[#0f172a] outline-none bg-transparent' />
                                        <button type='button' onClick={inc} aria-label='Agregar uno' className='w-11 h-full text-[18px] text-[#1e3a8a] font-semibold hover:text-[#0891b2]'>+</button>
                                    </div>
                                    <button type='button' onClick={add_card}
                                        className='h-[54px] rounded-[14px] bg-[#0891b2] hover:bg-[#0e7490] hover:-translate-y-px text-white font-bold text-[15px] flex items-center justify-center gap-[10px] transition-all shadow-[0_10px_24px_-12px_rgba(8,145,178,.6)]'>
                                        <Bag className='w-[18px] h-[18px]' strokeWidth={2} />Agregar al carrito
                                    </button>
                                    <button type='button' onClick={add_wishlist} aria-label={enWishlist ? 'Ver favoritos' : 'Agregar a favoritos'} aria-pressed={enWishlist}
                                        className={`w-[54px] h-[54px] rounded-[14px] border-[1.5px] grid place-items-center transition-all ${enWishlist ? 'bg-[#fef2f2] border-[#f87171] text-[#f87171] [&_path]:fill-current' : 'bg-white border-[#e8e6e1] text-[#1e3a8a] hover:border-[#f87171] hover:text-[#f87171]'}`}>
                                        <Heart className='w-5 h-5' />
                                    </button>
                                </div>
                                <button type='button' onClick={buynow}
                                    className='w-full h-[54px] rounded-[14px] bg-[#1e3a8a] hover:bg-[#172e6e] text-white font-bold text-[15px] transition-colors'>
                                    Comprar ahora
                                </button>
                            </>
                        ) : (
                            <div className='grid grid-cols-[minmax(0,1fr)_auto] gap-[10px] mt-6'>
                                <button type='button' disabled className='h-[54px] rounded-[14px] bg-[#f1efea] text-[#64748b] font-bold text-[15px] cursor-not-allowed'>
                                    Agotado
                                </button>
                                <button type='button' onClick={add_wishlist} aria-label={enWishlist ? 'Ver favoritos' : 'Agregar a favoritos'} aria-pressed={enWishlist}
                                    className={`w-[54px] h-[54px] rounded-[14px] border-[1.5px] grid place-items-center transition-all ${enWishlist ? 'bg-[#fef2f2] border-[#f87171] text-[#f87171] [&_path]:fill-current' : 'bg-white border-[#e8e6e1] text-[#1e3a8a] hover:border-[#f87171] hover:text-[#f87171]'}`}>
                                    <Heart className='w-5 h-5' />
                                </button>
                            </div>
                        )}

                        <div className='mt-6 border border-[#f1efea] rounded-[18px] overflow-hidden'>
                            {[
                                [Truck, bajoPedido ? 'Entrega bajo pedido' : 'Envío gratis', bajoPedido ? `Llega en hasta ${product.dias_entrega} días hábiles` : 'En compras desde Q899'],
                                [WhatsApp, 'Dudas antes de comprar', 'Te atendemos de 9:00 a 21:00'],
                                [Undo, 'Cambios fáciles', '30 días sin preguntas'],
                            ].map(([Icon, t, s]) => (
                                <div key={t} className='flex gap-[14px] items-center px-[18px] py-[15px] border-b last:border-b-0 border-[#f1efea]'>
                                    <div className='w-[38px] h-[38px] rounded-[11px] bg-[#ecfeff] text-[#0891b2] grid place-items-center flex-shrink-0'>
                                        <Icon className='w-[18px] h-[18px]' />
                                    </div>
                                    <div>
                                        <div className='text-[14px] font-bold text-[#0f172a]'>{t}</div>
                                        <div className='text-[12.5px] text-[#64748b]'>{s}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className='flex justify-between items-center gap-3 mt-5 text-[13px] text-[#64748b] flex-wrap'>
                            <span>{product.sku && <>SKU: {product.sku}</>}</span>
                            <div className='flex gap-[6px] items-center'>
                                <span className='mr-1'>Compartir</span>
                                {[['wa', 'WhatsApp', WhatsApp], ['fb', 'Facebook', Facebook], ['copy', 'Copiar enlace', LinkIcon]].map(([k, label, Icon]) => (
                                    <button key={k} type='button' onClick={() => share(k)} aria-label={label}
                                        className='w-[34px] h-[34px] rounded-full border border-[#e8e6e1] grid place-items-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-all'>
                                        <Icon className='w-[14px] h-[14px]' />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Complétalo con ── */}
                {comprable && sugeridos.length > 0 && (
                    <section className='pb-14'>
                        <div className='bg-[#faf9f6] border border-[#f1efea] rounded-[24px] p-8 sm:p-5'>
                            <h2 className='font-bold text-[#1e3a8a] text-[26px] m-0 tracking-[-.015em]' style={DISPLAY}>Complétalo con</h2>
                            <p className='mt-1 mb-6 text-[#64748b] text-[14px]'>Productos de {product.category ? `${product.category.toLowerCase()} ` : 'la misma categoría '}que combinan con este.</p>
                            <div className='grid grid-cols-[minmax(0,1fr)_280px] lg:grid-cols-1 gap-8 items-center'>
                                <div className='flex items-start gap-3 overflow-x-auto pb-1 [&>span]:self-center [&>span]:-mt-16'>
                                    {[{ ...product, price: precio, main: true }, ...sugeridos].map((p, i) => {
                                        const on = p.main || !comboOff[p._id]
                                        return (
                                            <React.Fragment key={p._id}>
                                                {i > 0 && <span className='text-[22px] text-[#64748b] font-light flex-shrink-0'>+</span>}
                                                <label className={`flex-1 min-w-[140px] flex flex-col gap-1 transition-opacity ${on ? 'opacity-100' : 'opacity-50'} ${p.main ? 'cursor-default' : 'cursor-pointer'}`}>
                                                    <input type='checkbox' className='sr-only' checked={on} disabled={p.main}
                                                        onChange={() => setComboOff(o => ({ ...o, [p._id]: !o[p._id] }))} />
                                                    <div className={`relative rounded-[16px] overflow-hidden bg-white border-[1.5px] mb-[6px] transition-colors ${on ? 'border-[#0891b2]' : 'border-[#f1efea]'}`} style={{ aspectRatio: '1 / 1' }}>
                                                        {(p.images?.[0] || p.image)
                                                            ? <img src={imgUrl(p.images?.[0] || p.image)} alt='' className='absolute inset-0 w-full h-full object-cover' />
                                                            : <span className='absolute inset-0 grid place-items-center text-[40px] opacity-30'>🐾</span>}
                                                        <span className={`absolute top-[10px] right-[10px] w-6 h-6 rounded-[7px] border-[1.5px] grid place-items-center ${on ? 'bg-[#0891b2] border-[#0891b2] text-white' : 'bg-white border-[#e8e6e1]'}`}>
                                                            {on && <Check className='w-[13px] h-[13px]' strokeWidth={3.5} />}
                                                        </span>
                                                    </div>
                                                    <span className='text-[10.5px] font-extrabold tracking-[.06em] uppercase text-[#0891b2]'>{p.main ? 'Este producto' : (p.category || p.brand || 'Sugerido')}</span>
                                                    <span className='text-[13.5px] font-bold text-[#0f172a] leading-[1.3]' style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.name}</span>
                                                    <span className='font-extrabold text-[#1e3a8a] text-[16px]' style={DISPLAY}>{priceLabel(p.price)}{p.main && quantity > 1 && p.price > 0 ? ` × ${quantity}` : ''}</span>
                                                </label>
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                                <div className='bg-white border border-[#f1efea] rounded-[18px] p-[22px] flex flex-col gap-[6px]'>
                                    <span className='text-[13px] text-[#64748b] font-semibold'>{comboSel.length + 1} productos</span>
                                    <span className='font-extrabold text-[#1e3a8a] text-[32px] tracking-[-.02em] mb-[10px]' style={DISPLAY}>
                                        {priceLabel(precio * quantity + comboSel.reduce((s, p) => s + Number(p.price || 0), 0))}
                                    </span>
                                    <button type='button' onClick={add_combo}
                                        className='w-full h-[54px] rounded-[14px] bg-[#0891b2] hover:bg-[#0e7490] text-white font-bold text-[15px] transition-colors shadow-[0_10px_24px_-12px_rgba(8,145,178,.6)]'>
                                        Agregar {comboSel.length ? 'todo' : ''} al carrito
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* ── Pestañas ── */}
                <section id='reviews' className='border-t border-[#f1efea] pt-10 scroll-mt-[160px]'>
                    <div role='tablist' className='flex gap-1 border-b border-[#e8e6e1] mb-8 overflow-x-auto overflow-y-hidden'>
                        {[['desc', 'Descripción'], ['spec', 'Especificaciones'], ['rev', 'Reseñas']].map(([k, label]) => (
                            <button key={k} role='tab' aria-selected={tab === k} onClick={() => setTab(k)}
                                className={`px-5 py-[14px] text-[15px] font-bold border-b-[2.5px] -mb-px whitespace-nowrap transition-colors ${tab === k ? 'text-[#1e3a8a] border-[#0891b2]' : 'text-[#64748b] border-transparent hover:text-[#0f172a]'}`}>
                                {label}
                                {k === 'rev' && <span className='text-[12px] bg-[#faf9f6] px-2 py-[2px] rounded-full ml-[6px] text-[#64748b]'>{reviewCount}</span>}
                            </button>
                        ))}
                    </div>

                    {tab === 'desc' && (
                        <div className='grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md-lg:grid-cols-1 gap-12'>
                            <div>
                                {(product.description || '').split(/\n+/).filter(Boolean).map((par, i) => (
                                    <p key={i} className='text-[15.5px] leading-[1.7] text-[#334155] mt-0 mb-4 max-w-[640px]'>{par}</p>
                                ))}
                                {!product.description && <p className='text-[15.5px] text-[#64748b] m-0'>Este producto aún no tiene descripción.</p>}
                            </div>
                            {product.shortDesc && (
                                <div>
                                    <h3 className='text-[20px] text-[#1e3a8a] font-bold mt-0 mb-3' style={DISPLAY}>En resumen</h3>
                                    <ul className='list-none p-0 m-0 grid gap-[10px]'>
                                        {product.shortDesc.split(/\n+|•|·/).map(s => s.trim()).filter(Boolean).map((s, i) => (
                                            <li key={i} className='flex gap-[10px] text-[14.5px] text-[#334155]'>
                                                <span className='w-[18px] h-[18px] flex-shrink-0 rounded-full bg-[#cffafe] text-[#0891b2] grid place-items-center mt-[2px]'>
                                                    <Check className='w-[10px] h-[10px]' strokeWidth={3.5} />
                                                </span>{s}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'spec' && (
                        <table className='w-full max-w-[720px] border-collapse text-[14.5px]'>
                            <tbody>
                                {specs.map(([k, v]) => (
                                    <tr key={k}>
                                        <td className='py-[14px] border-b border-[#f1efea] text-[#64748b] w-2/5'>{k}</td>
                                        <td className='py-[14px] border-b border-[#f1efea] text-[#0f172a] font-semibold'>{v}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {tab === 'rev' && <Reviews product={product} />}
                </section>
            </Container>

            {/* ── También te puede gustar ── */}
            {relacionados.length > 0 && (
                <section className='pt-16 pb-20'>
                    <Container>
                        <div className='flex justify-between items-center gap-[14px] mb-[18px]'>
                            <h3 className='font-bold text-[#1e3a8a] text-[26px] m-0 tracking-[-.015em]' style={DISPLAY}>También te puede gustar</h3>
                            <Link to={product.category ? `/products?category=${product.category}` : '/shops'} className='text-[14px] font-bold text-[#0891b2] hover:underline'>Ver más →</Link>
                        </div>
                        <div className='grid grid-cols-4 lg:grid-cols-2 gap-[14px]'>
                            {relacionados.map(p => <ProductCard key={p._id} product={p} />)}
                        </div>
                    </Container>
                </section>
            )}
            </>
            )}

            {zoom && current && (
                <div role='dialog' aria-modal='true' aria-label='Foto ampliada' onClick={() => setZoom(false)}
                    className='fixed inset-0 z-[10000] bg-[#0b1530]/85 grid place-items-center p-6'>
                    <img src={imgUrl(current)} alt={product.name} className='max-w-full max-h-full rounded-[18px] object-contain bg-white' />
                    <button type='button' onClick={() => setZoom(false)} aria-label='Cerrar'
                        className='absolute top-5 right-5 w-11 h-11 rounded-full bg-white grid place-items-center text-[#1e3a8a]'>
                        <Close className='w-5 h-5' />
                    </button>
                </div>
            )}

            <Footer/>
        </div>
    );
};

export default Details;
